import { Component, ViewEncapsulation, HostListener, OnDestroy, HostBinding } from '@angular/core';
import { Observable, of, forkJoin, Subscription, ReplaySubject, Subject,
	map, catchError, tap, switchMap, debounceTime, distinctUntilChanged } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor } from '@angular/common';

import { UserService } from '../../../../shared/services/user.service';
import { ProductConfiguration, CharacteristicValueSimpleObject } from '../../../models/product-configuration';
import { FN_FINISHING_LINE } from '../../../models/product-descriptor-rtpc';
import { ConfigurationService } from '../../../services/configuration.api.service';
import { ScrollService, ScrollAction } from '../../../../shared/services/scroll.service';
import {
	TemplateDescription,
	TemplateVariableArray,
	TemplateVariableString,
	TemplateVariableValues,
	ProductType,
	OfferProduct
} from '../../../models/product';
import { productFamilyParse, productDimensionFromProductConfiguration } from '../../../models/product-utils';
import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { TranslationService } from '../../../../shared/services/translation.service';
import { LocationService } from '../../../../shared/services/location.service';
import { AppWizardService } from '../../../../offer/services/app-wizard.service';
import { ProductTranslationService } from '../../../services/product-translation.service';
import { safe } from '../../../../shared/utils/safe';
import { ProductService } from '../../../services/product.api.service';
import { AppOfferService } from '../../../../offer/services/app-offer.service';
import { SoldToRelationshipGroup } from '../../../../shared/models/service';
import { BocClient } from '../../../../shared/models/boc-client-transac';
import { BackButtonComponent } from '../../../../shared/components/back-button/back-button.component';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { ScrollerDirective } from '../../../../shared/directives/scroller.directive';

import { FieldValueObject, addFieldValue } from './utils/field/field';
import { FieldObjectCst } from './utils/field/field-object';
import {
	dynObjectToMap,
	buildEditionProductCardFields
} from './utils/product-card-fields-builder';
import { FieldTextSelection } from './utils/field/field-text';
import { ProductCardContentComponent } from './product-card-content.component';

interface CustomType {
	productType: ProductType;
	soldTo: string;
	shipTo: string;
	endUser: string;
	serviceType: string;
}

const HIDDEN_VARIABLE_NAMES = new Set([FN_FINISHING_LINE]);
const FN_SHIP_TO_PARTY = 'shipToParty';
const FN_END_USER = 'endUser';
@Component({
	selector: 'copo-offer-product-edition-card',
	templateUrl: './product-edition-card.component.html',
	styleUrl: './product-edition-card.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		NgIf,
		LoadingSpinnerComponent,
		BackButtonComponent,
		TranslateModule,

		MatButtonModule,
		MatIconModule,

		MatFormFieldModule,

		MatSelectModule,

		MatOptionModule,
		NgFor,

		MatTooltipModule,
		ProductCardContentComponent
	]
})

export class ProductEditionCardComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	isSimulationInProgress = false;
	isLoading = true;
	isError = false;
	loadDataSubscription?: Subscription;
	hasUserSubmitterRole: boolean;
	selectedTemplate?: TemplateDescription;
	templates: TemplateDescription[] = [];
	configurationErrors: string[] = [];
	title = '';
	productFields?: FieldObjectCst;
	shipTo!: string;
	endUser!: string;
	serviceType!: string;
	readonly readOnlyUser?: boolean;
	selectedTemplateVariableValues!: TemplateVariableValues;
	productCode!: { code: string; value: string };
	userBocData?: SoldToRelationshipGroup[];
	bocs?: Map<string, BocClient>;
	soldToBocList?: BocClient[];
	soldToList?: string[];

	private readonly productFieldsSubject = new ReplaySubject<FieldObjectCst>(1);
	private readonly productId: string;
	private readonly productType: ProductType;
	private originalConfiguration?: ProductConfiguration;
	private currentConfiguration?: ProductConfiguration;
	private readonly soldTo!: string;

	private readonly variableValuesByTemplate = new Map<number, Map<string, TemplateVariableValues>>();
	private lastSimulatedConfigurationAsText = '';
	private subSimulationAndSubmission?: Subscription;
	private readonly focusSubject = new Subject<boolean>();
	private readonly subFocus: Subscription;
	private loadBocOfferSubscription?: Subscription;
	private originalFinishingLine?: CharacteristicValueSimpleObject[];

	constructor(
		private readonly route: ActivatedRoute,
		private readonly locationService: LocationService,
		private readonly productService: ProductService,
		private readonly appWizardService: AppWizardService,
		private readonly configurationService: ConfigurationService,
		private readonly scrollService: ScrollService,
		private readonly userService: UserService,
		private readonly translationService: TranslationService,
		private readonly appOfferService: AppOfferService,
		private readonly productTranslationService: ProductTranslationService
	) {
		const { state } = <{ state: unknown }> history;
		if (state !== null && typeof state === 'object') {
			const { productType, soldTo, shipTo, endUser, serviceType } = <Partial<CustomType>> state;
			this.soldTo = String(soldTo);
			this.shipTo = String(shipTo);
			this.endUser = String(endUser);
			this.serviceType = String(serviceType);
			if (productType) {
				this.productType = productType;
			}
		}
		this.getBocsOfferList();
		this.productId = <string> this.route.snapshot.params['productId'];
		this.productType = <ProductType> this.route.snapshot.params['productType'];
		const configurationNumber = <string | undefined > this.route.snapshot.params['configurationNumber'];
		this.hasUserSubmitterRole = this.userService.user.isOfferSubmitter();
		this.readOnlyUser = this.userService.user.readOnly;
		this.subFocus = this.focusSubject.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe({
			next: focusLost => {
				if (focusLost) {
					this.launchSimulation();
				}
			},
			error: (err: unknown) => {
				console.error('unable to listen focus', err);
			}
		});

		this.subSimulationAndSubmission = forkJoin([
			this.searchTemplate(this.productId)
				.pipe(switchMap(templateSearchResponse => {
					if (!templateSearchResponse || templateSearchResponse.results.length === 0) {
						return of(undefined);
					}
					return this.loadAllVariableValues(templateSearchResponse.results[0])
						.pipe(map(valuesLoaded => valuesLoaded ? templateSearchResponse : undefined));
				})),
			this.loadConfiguration(this.productId, configurationNumber, this.productType)
		])
			.pipe(switchMap(([templateSearchResponse, configuration]) => {
				const configurationVariableOnly = templateSearchResponse && configuration
					? this.createInitialConfiguration(
						templateSearchResponse.results[0],
						configuration
					) : undefined;
				this.originalConfiguration = configuration;
				return forkJoin([
					of(templateSearchResponse),
					of(configuration),
					!templateSearchResponse || !configurationVariableOnly
						? of(undefined)
						: this.simulate(templateSearchResponse.results[0], configurationVariableOnly)
				]);
			}))
			.subscribe({
				next: ([templateSearchResponse, configuration, simulationResponse]) => {
					this.isLoading = false;
					if (!templateSearchResponse) {
					// template not found
						this.isError = true;
						this.productService.showError('no template');
						return;
					}
					if (!configuration) {
					// product not found
						this.isError = true;
						this.productService.showError('no configuration');
						return;
					}
					if (simulationResponse?.errors) {
						this.configurationErrors = [...simulationResponse.errors];
						this.scrollService.setScroll(new ScrollAction(0));
					} else {
						this.configurationErrors = [];
					}
					this.templates = templateSearchResponse.results;
					this.selectedTemplate = this.templates[0];
					this.originalConfiguration = configuration;
					this.currentConfiguration = simulationResponse?.configuration || configuration;
					this.createFormAndLayout();
					this.lastSimulatedConfigurationAsText = this.extractSimpleConfigurationAsText();
					this.title = this.createTitle(this.originalConfiguration);
				},
				error: (err: unknown) => {
					this.handleError('unexpected error when loading product edition card', err);
				}
			});
	}

	@HostListener('focusout', ['$event']) eventFocusLostAnyField(evt: FocusEvent) {
		if (!evt.target || (<HTMLElement> evt.target).localName !== 'input') {
			return;
		}
		this.focusSubject.next(true);
	}

	@HostListener('focusin', ['$event']) eventFocusGainAnyField(evt: FocusEvent) {
		if (!evt.target || (<HTMLElement> evt.target).localName !== 'input') {
			return;
		}
		this.focusSubject.next(false);
	}

	ngOnDestroy() {
		this.subFocus.unsubscribe();
		if (this.subSimulationAndSubmission) {
			this.subSimulationAndSubmission.unsubscribe();
		}
		if (this.loadBocOfferSubscription) {
			this.loadBocOfferSubscription.unsubscribe();
		}
	}

	isAddToCartDisabled() {
		return (<FieldTextSelection>(this.productFields?.getField(FN_SHIP_TO_PARTY))).isOriginalValueDifferent()
			|| (<FieldTextSelection>(this.productFields?.getField(FN_END_USER))).isOriginalValueDifferent();
	}

	getProductFieldsObs() {
		return this.productFieldsSubject;
	}

	evtSubmitTemplateProduct(addToCart: boolean) {
		if (!this.selectedTemplate) {
			return;
		}
		if (this.subSimulationAndSubmission) {
			this.subSimulationAndSubmission.unsubscribe();
		}
		this.isLoading = true;
		this.isSimulationInProgress = false;
		this.subSimulationAndSubmission = this.submit(
			this.soldTo,
			this.currentConfiguration?.shipToParty?.replace(/^0+/, ''),
			this.currentConfiguration?.endUser?.replace(/^0+/, ''),
			this.productId,
			this.selectedTemplate,
			this.extractProductConfiguration(this.selectedTemplate)
		).subscribe({
			next: submissionResponse => {
				this.isLoading = false;
				if (!submissionResponse) {
					// unable to submit response
					this.productService.showError(this.translationService.getTR(
						'product.message.product.not.saved'
					));
					return;
				}
				if (submissionResponse.errors) {
					this.configurationErrors = [...submissionResponse.errors];
					this.scrollService.setScroll(new ScrollAction(0));
				} else {
					this.configurationErrors = [];
				}
				if (!submissionResponse.product) {
					this.productService.showError(this.translationService.getTR(
						'product.message.template.product.not.saved'
					));
					return;
				}
				this.productService.showMessage(this.translationService.getTR(
					'product.product.submitted'
				));
				this.appWizardService.getState().ui.setNewTemplateProduct(true);
				if (addToCart) {
					const requestedProduct: OfferProduct = {
						description: submissionResponse.product.description,
						ufoProductRef: submissionResponse.product.ufoProductRef,
						offerStatus: 'DRAFT',
						feasibilityStatus: 'PENDING',
						characteristics: submissionResponse.product.characteristics
					};
					this.appWizardService.getStateOfferForm().getFormProducts().addProduct(requestedProduct);
				}
				this.locationService.back();
			},
			error: (err: unknown) => {
				console.error('unexpected error when submitting configuration', err);
				this.productService.showError();
				this.isLoading = false;
			}
		});
	}

	evtTemplateChanged(evt: MatSelectChange) {
		const { originalConfiguration } = this;
		if (!originalConfiguration) {
			return;
		}
		if (this.subSimulationAndSubmission) {
			this.subSimulationAndSubmission.unsubscribe();
		}
		const templateNumber = <number> evt.value;
		this.selectedTemplate = this.templates[this.templates.findIndex(x => x.templateNumber === templateNumber)];
		this.isLoading = true;
		this.isError = false;
		this.subSimulationAndSubmission = forkJoin([
			this.simulate(this.selectedTemplate, originalConfiguration),
			this.loadAllVariableValues(this.selectedTemplate)
		]).subscribe({
			next: ([simulationResponse, variableValuesLoaded]) => {
				this.isLoading = false;
				if (!variableValuesLoaded) {
					// variable values not loaded
					return;
				}
				this.currentConfiguration = simulationResponse?.configuration || originalConfiguration;
				this.createFormAndLayout();
				this.lastSimulatedConfigurationAsText = this.extractSimpleConfigurationAsText();
			},
			error: (err: unknown) => {
				this.handleError('unexpected error', err);
			}
		});
	}

	private getBocsOfferList() {
		this.loadBocOfferSubscription = forkJoin([
			this.userService.getSoldToRelationshipGroupsTransac(this.serviceType)
				.pipe(switchMap(userBocData => {
					if (userBocData.length === 0) {
						return of(<const> [userBocData, <BocClient[]> []]);
					}
					const businessIds = Array.from(
						new Set(userBocData.flatMap(v => [v.soldTo, ...v.shipTos, ...v.endUsers]))
					);
					return forkJoin([
						of(userBocData),
						this.userService.getBocs(
							['businessId', 'name', 'city', 'address', 'country', 'postalCode'],
							businessIds.map(x => `businessId:${ x }`).join(' OR ')
						).pipe(map(v => v.results))
					]);
				}))
		]).subscribe({
			next: ([[userBocData, bocs]]) => {
				this.userBocData = userBocData;
				this.isLoading = false;
				const bocsMap = this.bocs = new Map(bocs.map(v => [v.businessId, v]));
				this.soldToList = this.userBocData.map(v => v.soldTo);
				this.soldToBocList = this.soldToList.map(v => safe(bocsMap.get(v)));
			},
			error: (err: unknown) => {
				console.error('error loading wizard parameters', err);
				this.isLoading = false;
				this.appWizardService.getState().errorInPage = true;
				this.appOfferService.showError(
					this.translationService.getTR('eoffer.wizard.parameters.error')
				);
			},
		});
	}

	private getSoldToList() {
		return this.soldToBocList !== undefined ? this.soldToBocList : [];
	}

	private getShipToList() {
		const soldToBusinessId = this.soldTo;
		const soldToGroup = soldToBusinessId && this.userBocData
			? this.userBocData.find(v => v.soldTo === soldToBusinessId)
			: undefined;
		const { bocs } = this;
		return soldToGroup && bocs
			? soldToGroup.shipTos.map(v => bocs.get(v)).filter((v): v is BocClient => v !== undefined)
			: [];
	}

	private getEndUserList() {
		const soldToBusinessId = this.soldTo;
		const soldToGroup = soldToBusinessId && this.userBocData
			? this.userBocData.find(v => v.soldTo === soldToBusinessId)
			: undefined;
		const { bocs } = this;
		return soldToGroup && bocs
			? soldToGroup.endUsers.map(v => bocs.get(v)).filter((v): v is BocClient => v !== undefined)
			: [];
	}

	private loadConfiguration(
		productId: string,
		configurationNumber: string | undefined,
		productType: ProductType
	) {
		// 1) retrieve configuration without direct translation (i.e. SIDE instead of INLINE)
		// 2) retrieve original finishing line (i.e. code of original configuration, without translation)
		// 3) translate directly (INLINE) configuration
		return this.configurationService.getConfigurationMappedOrNot(
			configurationNumber || productId,
			productType,
			'SIDE'
		)
			.pipe(
				tap(configuration => {
					this.originalFinishingLine = this.removeTranslationPrefix(
					<CharacteristicValueSimpleObject[]> configuration[FN_FINISHING_LINE]
					);
					if (this.originalFinishingLine[0]['id']) {
						this.originalFinishingLine[0]['id'] = undefined;
					}
				}),
				map(configuration => this.translateConfiguration(configuration)),
				catchError((err: unknown) => {
					console.error('unable to load configuration', err);
					return of(undefined);
				})
			);
	}

	private translateConfiguration(configuration: ProductConfiguration) {
		return Object.getOwnPropertyNames(configuration).reduce(
			(translatedConfugration, fieldName) => {
				const fieldValue = configuration[fieldName];
				translatedConfugration[fieldName] = typeof fieldValue === 'string'
					? this.productTranslationService.translate(fieldValue)
					: Array.isArray(fieldValue)
						? this.translateConfigurationCharacArray(fieldValue)
						: fieldValue;
				return translatedConfugration;
			},
			{ ...configuration }
		);
	}

	private translateConfigurationCharacArray(
		values: CharacteristicValueSimpleObject[]
	) {
		return values.map(value => {
			const translatedSimpleObject: CharacteristicValueSimpleObject = {};
			return Object.getOwnPropertyNames(value).reduce(
				(translatedValue, fieldName) => {
					const fieldValue = value[fieldName];
					translatedValue[fieldName] = typeof fieldValue === 'string'
						? this.productTranslationService.translate(fieldValue)
						: fieldValue;
					return translatedValue;
				},
				translatedSimpleObject
			);
		});
	}

	private removeTranslationPrefix(
		values: CharacteristicValueSimpleObject[]
	) {
		return values.map(value => {
			const valueWithoutPrefix: CharacteristicValueSimpleObject = {};
			return Object.getOwnPropertyNames(value).reduce(
				(objectWithoutPrefix, fieldName) => {
					const fieldValue = value[fieldName];
					objectWithoutPrefix[fieldName] = typeof fieldValue === 'string'
						? fieldValue.slice(Math.max(0, fieldValue.lastIndexOf('.') + 1))
						: fieldValue;
					return objectWithoutPrefix;
				},
				valueWithoutPrefix
			);
		});
	}

	private searchTemplate(productRef: string) {
		return this.configurationService.searchTemplate(productRef, true, 'INLINE')
			.pipe(catchError((err: unknown) => {
				console.error('unable to search template', err);
				return of(undefined);
			}));
	}

	private loadAllVariableValues(template: TemplateDescription) {
		if (this.variableValuesByTemplate.get(template.templateNumber) !== undefined) {
			return of(true);
		}
		const variableValueObservables: Observable<boolean>[] = [];
		for (const variableName of Object.getOwnPropertyNames(template.properties)) {
			const variable = template.properties[variableName];
			if (variable.type === 'string' && !(<TemplateVariableString> variable).free) {
				variableValueObservables.push(this.loadVariableValues(template.templateNumber, variableName));
			} else if (variable.type === 'array') {
				const variableArray = <TemplateVariableArray> variable;
				for (const entryVariableName of Object.getOwnPropertyNames(variableArray.properties)) {
					const entryVariable = variableArray.properties[entryVariableName];
					if (entryVariable.type !== 'string' || (<TemplateVariableString> entryVariable).free) {
						continue;
					}
					variableValueObservables.push(this.loadVariableValues(template.templateNumber, entryVariableName));
				}
			}
		}
		return forkJoin(variableValueObservables).pipe(map(results => results.filter(isOk => !isOk).length === 0));
	}

	private loadVariableValues(templateNumber: number, variableName: string) {
		return this.configurationService.getVariableValues(templateNumber, variableName)
			.pipe(
				tap(values => {
					let templateVariableValues = this.variableValuesByTemplate.get(templateNumber);
					if (templateVariableValues === undefined) {
						templateVariableValues = new Map<string, TemplateVariableValues>();
						this.variableValuesByTemplate.set(templateNumber, templateVariableValues);
					}
					const list = [];
					switch (variableName) {
					case 'endUser':
						for (const endUserList of this.getEndUserList()) {
							this.productCode = { code: endUserList.businessId.padStart(10, '0'),
								value: `${ endUserList.name } / ${ endUserList.city }` };
							list.push(this.productCode);
						}
						this.selectedTemplateVariableValues = { orderBookValues: list, otherValues: [] };
						templateVariableValues.set(variableName, this.selectedTemplateVariableValues);
						break;
					case 'shipToParty':
						for (const shipToList of this.getShipToList()) {
							this.productCode = { code: shipToList.businessId.padStart(10, '0'),
								value: `${ shipToList.name } / ${ shipToList.city }` };
							list.push(this.productCode);
						}
						this.selectedTemplateVariableValues = { orderBookValues: list, otherValues: [] };
						templateVariableValues.set(variableName, this.selectedTemplateVariableValues);
						break;
					default:
						templateVariableValues.set(variableName, values);
					}
				}),
				map(() => true),
				catchError((err: unknown) => {
					console.error(
						`unable to load values for variable '${ variableName }' of template '${ templateNumber }'`,
						err
					);
					return of(false);
				})
			);
	}

	private launchSimulation() {
		if (!this.selectedTemplate) {
			return;
		}
		const newSimpleConfigurationAsText = this.extractSimpleConfigurationAsText();
		if (this.lastSimulatedConfigurationAsText === newSimpleConfigurationAsText) {
			return;
		}
		if (this.isLoading) { // already a submission in progress
			return;
		}
		this.lastSimulatedConfigurationAsText = newSimpleConfigurationAsText;
		if (this.subSimulationAndSubmission) {
			this.subSimulationAndSubmission.unsubscribe();
		}
		this.subSimulationAndSubmission = this.simulate(
			this.selectedTemplate,
			this.extractProductConfiguration(this.selectedTemplate)
		).subscribe({
			next: simulationResponse => {
				if (!simulationResponse) {
					this.productService.showError();
					return;
				}
				if (simulationResponse.errors) {
					this.configurationErrors = [...simulationResponse.errors];
					this.scrollService.setScroll(new ScrollAction(0));
				} else {
					this.configurationErrors = [];
				}
				if (!simulationResponse.configuration) {
					return;
				}
				this.currentConfiguration = simulationResponse.configuration;
				this.hasUserSubmitterRole = this.userService.user.isOfferSubmitter();
				// remove hidden fields
				this.currentConfiguration.finishingLine = undefined;
				this.lastSimulatedConfigurationAsText = this.extractSimpleConfigurationAsText();
			}
		});
	}

	private simulate(
		template: TemplateDescription,
		configuration: ProductConfiguration | FieldValueObject
	) {
		this.isSimulationInProgress = true;
		return this.configurationService.simulateTemplateProduct(
			{
				templateReference: String(template.templateNumber),
				configuration
			},
			'INLINE'
		).pipe(
			catchError((err: unknown) => {
				console.error('unable to simulate', err);
				return of(undefined);
			}),
			tap({
				next: () => {
					this.isSimulationInProgress = false;
				},
				error: () => {
					this.isSimulationInProgress = false;
				}
			})
		);
	}

	private submit(
		soldTo: string,
		shipTo: string | undefined,
		endUser: string | undefined,
		productId: string,
		template: TemplateDescription,
		partialConfiguration: FieldValueObject
	) {
		if (!this.currentConfiguration) {
			throw new Error('no current configuration');
		}
		const family = productFamilyParse(this.currentConfiguration.familyCode);
		return this.configurationService.submitTemplateProduct(
			{
				soldTo,
				shipTo,
				endUser,
				configuration: partialConfiguration,
				templateRef: String(template.templateNumber),
				description: {
					productRef: String(productId),
					configurationRef: this.currentConfiguration.configurationNumber !== undefined
						? String(this.currentConfiguration.configurationNumber).padStart(12, '0')
						: undefined,
					customerArticle: this.currentConfiguration.usePartReference,
					family,
					steelGrade: this.currentConfiguration.steelGradeCode,
					dimension: productDimensionFromProductConfiguration(this.currentConfiguration)
				}
			},
			this.serviceType,
			'INLINE'
		).pipe(catchError((err: unknown) => {
			console.error('unable to submit configuration', err);
			return of(undefined);
		}));
	}

	private getBocsUserValue(userCode: string | undefined, userList: BocClient[]) {
		for (const list of userList) {
			if (list.businessId.padStart(10, '0') === userCode) {
				return `${ list.name } / ${ list.city }`;
			}
		}
		return '';
	}

	private createFormAndLayout() {
		if (!this.selectedTemplate || !this.originalConfiguration || !this.currentConfiguration) {
			return;
		}
		const variableValues = this.variableValuesByTemplate.get(this.selectedTemplate.templateNumber);
		this.productFields = buildEditionProductCardFields(
			this.getBocsUserValue(this.originalConfiguration.soldToParty, this.getSoldToList()),
			this.getBocsUserValue(this.originalConfiguration.shipToParty, this.getShipToList()),
			this.getBocsUserValue(this.originalConfiguration.endUser, this.getEndUserList()),
			this.originalConfiguration,
			this.currentConfiguration,
			dynObjectToMap(this.selectedTemplate.properties),
			variableValues || new Map<string, TemplateVariableValues>(),
			characteristiName => !HIDDEN_VARIABLE_NAMES.has(characteristiName)
		);
		this.productFieldsSubject.next(this.productFields);
	}

	private extractSimpleConfigurationAsText() {
		return JSON.stringify(safe(this.productFields).value());
	}

	private extractProductConfiguration(selectedTemplate: TemplateDescription) {
		const partialConfiguration = safe(this.productFields).extractVarValue();
		this.completeConfigurationWithHiddenValues(
			selectedTemplate,
			safe(this.originalConfiguration),
			partialConfiguration
		);
		return partialConfiguration;
	}

	private completeConfigurationWithHiddenValues(
		template: TemplateDescription,
		originalConfiguration: ProductConfiguration,
		partialConfiguration: FieldValueObject
	) {
		if (
			!(FN_FINISHING_LINE in template.properties)
			|| !originalConfiguration.finishingLine
			|| !this.originalFinishingLine
		) {
			return;
		}

		addFieldValue(
			template.properties[FN_FINISHING_LINE].path,
			this.originalFinishingLine,
			partialConfiguration
		);
	}

	private createTitle(product: ProductConfiguration) {
		const userPartReference = product.usePartReference;
		const userPartReferenceTitle = userPartReference ? `${ userPartReference } - ` : '';
		return `${
			userPartReferenceTitle
		}${
			product.subcategory
		} & ${
			product.form
		}`;
	}

	private handleError(message: string, err: unknown) {
		this.isLoading = false;
		this.isError = true;
		console.error(message, err);
		this.productService.showError();
	}

	private createInitialConfiguration(
		template: TemplateDescription,
		configuration: ProductConfiguration
	) {
		// filtered configuration <- keep only variables
		const variableValues = this.variableValuesByTemplate.get(template.templateNumber);
		const productFields = buildEditionProductCardFields(
			this.getBocsUserValue(configuration.soldToParty, this.getSoldToList()),
			this.getBocsUserValue(configuration.shipToParty, this.getShipToList()),
			this.getBocsUserValue(configuration.endUser, this.getEndUserList()),
			configuration,
			configuration,
			dynObjectToMap(template.properties),
			variableValues || new Map<string, TemplateVariableValues>(),
			() => true
		);
		const configurationVariableOnly = productFields.extractVarValue();
		this.completeConfigurationWithHiddenValues(template, configuration, configurationVariableOnly);
		return configurationVariableOnly;
	}
}
